import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Label } from '@/app/components/ui/label';
import { useCreateSafetyTemplate } from '@/hooks/use-safety';
import { OfflineIndicator } from '@/app/components/offline/offline-indicator';
import type { SafetyTemplateType } from '@/lib/types';

export function CreateTemplatePage() {
  const navigate = useNavigate();
  const createMutation = useCreateSafetyTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateType, setTemplateType] = useState<SafetyTemplateType>('jsa');
  const [defaultHazards, setDefaultHazards] = useState('');
  const [defaultPpe, setDefaultPpe] = useState('');
  const [defaultEmergencyProcedures, setDefaultEmergencyProcedures] = useState('');
  const [defaultRiggingPlan, setDefaultRiggingPlan] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({
        name,
        description,
        template_type: templateType,
        default_hazards: defaultHazards ? JSON.parse(defaultHazards) : [],
        default_ppe_requirements: defaultPpe,
        default_emergency_procedures: defaultEmergencyProcedures,
        default_rigging_plan: defaultRiggingPlan,
      });
      navigate('/safety/templates');
    } catch {
      // error handled by mutation
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/safety/templates')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Safety Template</h1>
          <p className="text-gray-600">Create a reusable template for safety documents</p>
        </div>
      </div>

      <OfflineIndicator />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Template Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Standard High-Rise Window Replacement"
                required
              />
            </div>
            <div>
              <Label htmlFor="type">Template Type</Label>
              <Select
                value={templateType}
                onValueChange={(value) => setTemplateType(value as SafetyTemplateType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="jsa">JSA</SelectItem>
                  <SelectItem value="safe_work_plan">Safe Work Plan</SelectItem>
                  <SelectItem value="rescue_plan">Rescue Plan</SelectItem>
                  <SelectItem value="full_package">Full Package</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Describe when to use this template..."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Default Content</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="hazards">Default Hazards (JSON array)</Label>
              <Textarea
                id="hazards"
                value={defaultHazards}
                onChange={(e) => setDefaultHazards(e.target.value)}
                rows={4}
                placeholder='[{"step_number": 1, "task_description": "Working at height", "hazard_description": "Fall from elevation", "risk_level": "high", "control_measures": "Full harness, twin-tail lanyard, anchor point rated to 15kN", "residual_risk_level": "low"}]'
              />
            </div>
            <div>
              <Label htmlFor="ppe">Default PPE Requirements</Label>
              <Textarea
                id="ppe"
                value={defaultPpe}
                onChange={(e) => setDefaultPpe(e.target.value)}
                rows={2}
                placeholder="Hard hat, safety glasses, gloves, harness..."
              />
            </div>
            <div>
              <Label htmlFor="emergency">Default Emergency Procedures</Label>
              <Textarea
                id="emergency"
                value={defaultEmergencyProcedures}
                onChange={(e) => setDefaultEmergencyProcedures(e.target.value)}
                rows={2}
              />
            </div>
            {(templateType === 'safe_work_plan' || templateType === 'full_package') && (
              <div>
                <Label htmlFor="rigging">Default Rigging Plan</Label>
                <Textarea
                  id="rigging"
                  value={defaultRiggingPlan}
                  onChange={(e) => setDefaultRiggingPlan(e.target.value)}
                  rows={3}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/safety/templates')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Template'}
          </Button>
        </div>
      </form>
    </div>
  );
}
